'use client'

import React, { useState, useEffect } from 'react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Textarea } from '../ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Progress } from '../ui/progress'
import { Alert, AlertDescription } from '../ui/alert'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs'
import { Label } from '../ui/label'
import { Separator } from '../ui/separator'
import {
  Target,
  Users,
  TrendingUp,
  Lightbulb,
  ArrowRight,
  Plus,
  X,
  Download,
  Wand2,
  AlertCircle,
  CheckCircle,
  Star,
  Zap,
  Eye,
  Brain,
  Award
} from 'lucide-react'
import { Brand } from '../../utils/mockData'

interface BrandPositioningProps {
  brand: Brand
}

interface Competitor {
  id: string
  name: string
  strengths: string[]
  weaknesses: string[]
  positioning: string
  targetAudience: string
  keyMessages: string[]
}

interface Audience {
  id: string
  name: string
  demographics: string
  psychographics: string
  painPoints: string[]
  goals: string[]
  channels: string[]
}

interface ValueProposition {
  id: string
  statement: string
  targetSegment: string
  keyBenefits: string[]
  differentiators: string[]
  proof: string[]
}

const POSITIONING_FRAMEWORKS = [
  {
    id: 'classic',
    name: 'Classic Positioning',
    description: 'For [target audience], [brand] is the [category] that [key benefit] because [reason to believe]',
    example: 'For busy professionals, Starbucks is the coffee experience that provides premium quality and convenience because of our expertly crafted beverages and widespread locations.'
  },
  {
    id: 'jobs-to-be-done',
    name: 'Jobs-to-be-Done',
    description: 'When [situation], I want [motivation], so I can [expected outcome]',
    example: 'When I need to stay productive during a busy workday, I want a premium coffee experience, so I can feel energized and focused.'
  },
  {
    id: 'benefit-focused',
    name: 'Benefit-Focused',
    description: '[Brand] helps [target audience] achieve [primary benefit] through [unique approach]',
    example: 'Tesla helps environmentally conscious consumers achieve sustainable transportation through innovative electric vehicle technology.'
  }
]

const COMPETITIVE_DIMENSIONS = [
  'Price',
  'Quality',
  'Innovation',
  'Customer Service',
  'Brand Reputation',
  'Market Share',
  'Product Features',
  'User Experience',
  'Accessibility',
  'Sustainability'
]

export function BrandPositioning({ brand }: BrandPositioningProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [activeTab, setActiveTab] = useState('positioning')

  // Positioning Data
  const [positioningStatement, setPositioningStatement] = useState('')
  const [selectedFramework, setSelectedFramework] = useState('classic')
  const [targetAudience, setTargetAudience] = useState('')
  const [keyBenefit, setKeyBenefit] = useState('')
  const [reasonToBelieve, setReasonToBelieve] = useState('')
  const [category, setCategory] = useState('')
  
  // Competitive Analysis
  const [competitors, setCompetitors] = useState<Competitor[]>([])
  const [selectedDimensions, setSelectedDimensions] = useState<string[]>(['Price', 'Quality', 'Innovation'])
  
  // Value Propositions
  const [valuePropositions, setValuePropositions] = useState<ValueProposition[]>([])
  
  // Audience Definitions
  const [audiences, setAudiences] = useState<Audience[]>([])

  useEffect(() => {
    loadPositioningData()
  }, [brand.id])

  const loadPositioningData = () => {
    try {
      const savedData = localStorage.getItem(`brandPositioning_${brand.id}`)
      if (savedData) {
        const data = JSON.parse(savedData)
        setPositioningStatement(data.positioningStatement || '')
        setSelectedFramework(data.selectedFramework || 'classic')
        setTargetAudience(data.targetAudience || '')
        setKeyBenefit(data.keyBenefit || '')
        setReasonToBelieve(data.reasonToBelieve || '')
        setCategory(data.category || '')
        setCompetitors(data.competitors || [])
        setSelectedDimensions(data.selectedDimensions || ['Price', 'Quality', 'Innovation'])
        setValuePropositions(data.valuePropositions || [])
        setAudiences(data.audiences || [])
      }
    } catch (error) {
      console.error('Failed to load positioning data:', error)
    }
  }

  const savePositioningData = async () => {
    setIsLoading(true)
    try {
      const data = {
        positioningStatement,
        selectedFramework,
        targetAudience,
        keyBenefit,
        reasonToBelieve,
        category,
        competitors,
        selectedDimensions,
        valuePropositions,
        audiences,
        lastUpdated: new Date().toISOString()
      }
      
      localStorage.setItem(`brandPositioning_${brand.id}`, JSON.stringify(data))
      setHasUnsavedChanges(false)
      
      // Show success feedback
      setTimeout(() => setIsLoading(false), 500)
    } catch (error) {
      console.error('Failed to save positioning data:', error)
      setIsLoading(false)
    }
  }

  const generateAIPositioning = async () => {
    setIsLoading(true)
    try {
      // Simulate AI generation
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      const framework = POSITIONING_FRAMEWORKS.find(f => f.id === selectedFramework)
      if (framework) {
        let aiStatement = framework.description
        
        if (targetAudience) {
          aiStatement = aiStatement.replace('[target audience]', targetAudience)
        }
        if (brand.name) {
          aiStatement = aiStatement.replace('[brand]', brand.name)
        }
        if (category) {
          aiStatement = aiStatement.replace('[category]', category)
        }
        if (keyBenefit) {
          aiStatement = aiStatement.replace('[key benefit]', keyBenefit)
          aiStatement = aiStatement.replace('[primary benefit]', keyBenefit)
        }
        if (reasonToBelieve) {
          aiStatement = aiStatement.replace('[reason to believe]', reasonToBelieve)
          aiStatement = aiStatement.replace('[unique approach]', reasonToBelieve)
        }
        
        setPositioningStatement(aiStatement)
        setHasUnsavedChanges(true)
      }
    } catch (error) {
      console.error('Failed to generate AI positioning:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const addCompetitor = () => {
    const newCompetitor: Competitor = {
      id: Date.now().toString(),
      name: '',
      strengths: [],
      weaknesses: [],
      positioning: '',
      targetAudience: '',
      keyMessages: []
    }
    setCompetitors([...competitors, newCompetitor])
    setHasUnsavedChanges(true)
  }

  const updateCompetitor = (id: string, updates: Partial<Competitor>) => {
    setCompetitors(competitors.map(comp => 
      comp.id === id ? { ...comp, ...updates } : comp
    ))
    setHasUnsavedChanges(true)
  }

  const removeCompetitor = (id: string) => {
    setCompetitors(competitors.filter(comp => comp.id !== id))
    setHasUnsavedChanges(true)
  }

  const addValueProposition = () => {
    const newVP: ValueProposition = {
      id: Date.now().toString(),
      statement: '',
      targetSegment: '',
      keyBenefits: [],
      differentiators: [],
      proof: []
    }
    setValuePropositions([...valuePropositions, newVP])
    setHasUnsavedChanges(true)
  }

  const updateValueProposition = (id: string, updates: Partial<ValueProposition>) => {
    setValuePropositions(valuePropositions.map(vp => 
      vp.id === id ? { ...vp, ...updates } : vp
    ))
    setHasUnsavedChanges(true)
  }

  const removeValueProposition = (id: string) => {
    setValuePropositions(valuePropositions.filter(vp => vp.id !== id))
    setHasUnsavedChanges(true)
  }

  const addAudience = () => {
    const newAudience: Audience = {
      id: Date.now().toString(),
      name: '',
      demographics: '',
      psychographics: '',
      painPoints: [],
      goals: [],
      channels: []
    }
    setAudiences([...audiences, newAudience])
    setHasUnsavedChanges(true)
  }

  const updateAudience = (id: string, updates: Partial<Audience>) => {
    setAudiences(audiences.map(aud => 
      aud.id === id ? { ...aud, ...updates } : aud
    ))
    setHasUnsavedChanges(true)
  }

  const removeAudience = (id: string) => {
    setAudiences(audiences.filter(aud => aud.id !== id))
    setHasUnsavedChanges(true)
  }

  const exportPositioning = () => {
    const data = {
      brand: brand.name,
      positioningStatement,
      framework: selectedFramework,
      targetAudience,
      keyBenefit,
      reasonToBelieve,
      category,
      competitors,
      valuePropositions,
      audiences,
      exportedAt: new Date().toISOString()
    }
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${brand.name}-positioning-analysis.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const calculateCompletionProgress = () => {
    let completed = 0
    let total = 0
    
    // Positioning section
    total += 5
    if (positioningStatement) completed++
    if (targetAudience) completed++
    if (keyBenefit) completed++
    if (reasonToBelieve) completed++
    if (category) completed++
    
    // Competitive analysis
    total += 2
    if (competitors.length > 0) completed++
    if (selectedDimensions.length > 0) completed++
    
    // Value propositions
    total += 1
    if (valuePropositions.length > 0) completed++
    
    // Audience definitions
    total += 1
    if (audiences.length > 0) completed++
    
    return Math.round((completed / total) * 100)
  }

  const progress = calculateCompletionProgress()

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
              <Target className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Brand Positioning Template</h1>
              <p className="text-slate-600">Define your competitive positioning and value propositions</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="outline" onClick={exportPositioning}>
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            <Button 
              onClick={savePositioningData}
              disabled={isLoading}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {/* Progress Bar */}
        <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-800">Completion Progress</span>
              <span className="text-sm text-blue-600">{progress}%</span>
            </div>
            <Progress value={progress} className="h-2 bg-blue-100" />
          </CardContent>
        </Card>

        {hasUnsavedChanges && (
          <Alert className="mt-4 border-yellow-200 bg-yellow-50">
            <AlertCircle className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-yellow-800">
              You have unsaved changes. Don't forget to save your progress.
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="positioning" className="flex items-center gap-2">
            <Target className="w-4 h-4" />
            Positioning
          </TabsTrigger>
          <TabsTrigger value="competitive" className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Competitive
          </TabsTrigger>
          <TabsTrigger value="value-props" className="flex items-center gap-2">
            <Award className="w-4 h-4" />
            Value Props
          </TabsTrigger>
          <TabsTrigger value="audience" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Audience
          </TabsTrigger>
        </TabsList>

        {/* Positioning Tab */}
        <TabsContent value="positioning" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5" />
                Brand Positioning Statement
              </CardTitle>
              <CardDescription>
                Create a clear, concise statement that defines your brand's unique position in the market
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Framework Selection */}
              <div className="space-y-4">
                <Label>Choose a Positioning Framework</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {POSITIONING_FRAMEWORKS.map((framework) => (
                    <Card 
                      key={framework.id}
                      className={`cursor-pointer transition-all ${
                        selectedFramework === framework.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => {
                        setSelectedFramework(framework.id)
                        setHasUnsavedChanges(true)
                      }}
                    >
                      <CardContent className="p-4">
                        <h4 className="font-medium mb-2">{framework.name}</h4>
                        <p className="text-sm text-slate-600 mb-3">{framework.description}</p>
                        <div className="bg-slate-100 p-3 rounded text-xs text-slate-700">
                          <strong>Example:</strong> {framework.example}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Framework Inputs */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="target-audience">Target Audience</Label>
                  <Input
                    id="target-audience"
                    placeholder="busy professionals, tech-savvy millennials, etc."
                    value={targetAudience}
                    onChange={(e) => {
                      setTargetAudience(e.target.value)
                      setHasUnsavedChanges(true)
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Category/Market</Label>
                  <Input
                    id="category"
                    placeholder="coffee experience, electric vehicles, etc."
                    value={category}
                    onChange={(e) => {
                      setCategory(e.target.value)
                      setHasUnsavedChanges(true)
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="key-benefit">Key Benefit</Label>
                  <Input
                    id="key-benefit"
                    placeholder="premium quality and convenience, sustainable transportation, etc."
                    value={keyBenefit}
                    onChange={(e) => {
                      setKeyBenefit(e.target.value)
                      setHasUnsavedChanges(true)
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reason-believe">Reason to Believe</Label>
                  <Input
                    id="reason-believe"
                    placeholder="expertly crafted beverages, innovative technology, etc."
                    value={reasonToBelieve}
                    onChange={(e) => {
                      setReasonToBelieve(e.target.value)
                      setHasUnsavedChanges(true)
                    }}
                  />
                </div>
              </div>

              {/* AI Generation */}
              <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-3">
                  <Brain className="w-5 h-5 text-purple-600" />
                  <div>
                    <p className="font-medium text-purple-800">AI-Powered Positioning</p>
                    <p className="text-sm text-purple-600">Generate a positioning statement based on your inputs</p>
                  </div>
                </div>
                <Button
                  onClick={generateAIPositioning}
                  disabled={isLoading || !targetAudience || !keyBenefit}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                >
                  <Wand2 className="w-4 h-4 mr-2" />
                  {isLoading ? 'Generating...' : 'Generate'}
                </Button>
              </div>

              {/* Final Positioning Statement */}
              <div className="space-y-2">
                <Label htmlFor="positioning-statement">Final Positioning Statement</Label>
                <Textarea
                  id="positioning-statement"
                  placeholder="Your brand positioning statement will appear here..."
                  value={positioningStatement}
                  onChange={(e) => {
                    setPositioningStatement(e.target.value)
                    setHasUnsavedChanges(true)
                  }}
                  rows={4}
                  className="resize-none"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Competitive Analysis Tab */}
        <TabsContent value="competitive" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Competitive Analysis
                  </CardTitle>
                  <CardDescription>
                    Analyze your competitors to identify opportunities for differentiation
                  </CardDescription>
                </div>
                <Button onClick={addCompetitor} size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Competitor
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Analysis Dimensions */}
              <div className="space-y-4">
                <Label>Analysis Dimensions</Label>
                <div className="flex flex-wrap gap-2">
                  {COMPETITIVE_DIMENSIONS.map((dimension) => (
                    <Badge
                      key={dimension}
                      variant={selectedDimensions.includes(dimension) ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => {
                        if (selectedDimensions.includes(dimension)) {
                          setSelectedDimensions(selectedDimensions.filter(d => d !== dimension))
                        } else {
                          setSelectedDimensions([...selectedDimensions, dimension])
                        }
                        setHasUnsavedChanges(true)
                      }}
                    >
                      {dimension}
                    </Badge>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Competitors List */}
              <div className="space-y-4">
                {competitors.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <TrendingUp className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No competitors added yet</p>
                    <p className="text-sm">Click "Add Competitor" to start your analysis</p>
                  </div>
                ) : (
                  competitors.map((competitor) => (
                    <Card key={competitor.id} className="border-slate-200">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-4">
                          <Input
                            placeholder="Competitor name"
                            value={competitor.name}
                            onChange={(e) => updateCompetitor(competitor.id, { name: e.target.value })}
                            className="font-medium max-w-xs"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeCompetitor(competitor.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Positioning</Label>
                            <Textarea
                              placeholder="How do they position themselves?"
                              value={competitor.positioning}
                              onChange={(e) => updateCompetitor(competitor.id, { positioning: e.target.value })}
                              rows={3}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Target Audience</Label>
                            <Textarea
                              placeholder="Who do they target?"
                              value={competitor.targetAudience}
                              onChange={(e) => updateCompetitor(competitor.id, { targetAudience: e.target.value })}
                              rows={3}
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Value Propositions Tab */}
        <TabsContent value="value-props" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="w-5 h-5" />
                    Value Propositions
                  </CardTitle>
                  <CardDescription>
                    Define clear value propositions for different customer segments
                  </CardDescription>
                </div>
                <Button onClick={addValueProposition} size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Value Prop
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {valuePropositions.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Award className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No value propositions defined yet</p>
                  <p className="text-sm">Create clear statements of value for your customers</p>
                </div>
              ) : (
                valuePropositions.map((vp, index) => (
                  <Card key={vp.id} className="border-slate-200">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">Value Proposition #{index + 1}</CardTitle>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeValueProposition(vp.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Value Statement</Label>
                          <Textarea
                            placeholder="Clear statement of value..."
                            value={vp.statement}
                            onChange={(e) => updateValueProposition(vp.id, { statement: e.target.value })}
                            rows={3}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Target Segment</Label>
                          <Textarea
                            placeholder="Who is this value prop for?"
                            value={vp.targetSegment}
                            onChange={(e) => updateValueProposition(vp.id, { targetSegment: e.target.value })}
                            rows={3}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audience Tab */}
        <TabsContent value="audience" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Target Audience Definitions
                  </CardTitle>
                  <CardDescription>
                    Define detailed profiles of your target audience segments
                  </CardDescription>
                </div>
                <Button onClick={addAudience} size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Audience
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {audiences.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No audience segments defined yet</p>
                  <p className="text-sm">Create detailed profiles of your target customers</p>
                </div>
              ) : (
                audiences.map((audience, index) => (
                  <Card key={audience.id} className="border-slate-200">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Input
                            placeholder="Audience segment name"
                            value={audience.name}
                            onChange={(e) => updateAudience(audience.id, { name: e.target.value })}
                            className="font-medium max-w-xs"
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAudience(audience.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Demographics</Label>
                          <Textarea
                            placeholder="Age, location, income, education..."
                            value={audience.demographics}
                            onChange={(e) => updateAudience(audience.id, { demographics: e.target.value })}
                            rows={3}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Psychographics</Label>
                          <Textarea
                            placeholder="Values, interests, lifestyle..."
                            value={audience.psychographics}
                            onChange={(e) => updateAudience(audience.id, { psychographics: e.target.value })}
                            rows={3}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}